import React, { useEffect, useMemo, useState } from 'react';
import { CardHeader } from '../ui/Card';
import { Transaction, CoinListItem, Holding, PriceMap } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { useCoinList } from '../../hooks/useCoinList';
import { CoinSelector } from './CoinSelector';
import { WalletSelector, PRESET_WALLETS } from './WalletSelector';
import { loadCoinImages } from '../../utils/coinImageCache';
import { captureEntryFxForDate, captureFeeForDate, captureTransferFxForDate } from '../../utils/fxCapture';
import { PlusCircle, ArrowRightLeft } from 'lucide-react';
import {
  maxSellableAt,
  walletsForCoin,
  formatDateAU,
  WALLET_EPS,
} from '../../utils/walletTimeline';
import { InfoTooltip } from '../ui/InfoTooltip';

interface TransactionFormProps {
  onAdd: (tx: Omit<Transaction, 'id' | 'timestamp'>) => void;
  metals?: CoinListItem[];
  transactions?: Transaction[];
  holdings?: Holding[];
  /** App-wide live prices (crypto + metals + stocks), USD-keyed by asset id.
   *  Drives the "Current: …" helper under the Price field for any asset type. */
  prices?: PriceMap;
}

type TxType = 'BUY' | 'SELL' | 'TRANSFER';

// Coin amounts shown to the precision used elsewhere in the app — up to 6
// decimals, with thousands separators and no forced trailing zeros.
function fmtCoins(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 6 });
}

/** Inline "Use max" beside a limit message: the fix is one tap, not arithmetic. */
function UseMax({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-1.5 underline underline-offset-2 font-medium"
      style={{ color: 'inherit' }}
    >
      Use max
    </button>
  );
}

const SELL_WARN_COLOR = '#c4615e'; // muted coral (warning)
const SELL_HINT_COLOR = '#b89968'; // bronze (available helper)

// All wallets known to the app — preset list + any wallets used in past transactions.
function collectWallets(txs: Transaction[]): string[] {
  const set = new Set<string>(PRESET_WALLETS);
  for (const tx of txs) {
    if (tx.wallet)     set.add(tx.wallet);
    if (tx.fromWallet) set.add(tx.fromWallet);
    if (tx.toWallet)   set.add(tx.toWallet);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function TransactionForm({ onAdd, metals = [], transactions = [], holdings = [], prices = {} }: TransactionFormProps) {
  const today = new Date().toISOString().split('T')[0];
  const { fmt, currency, rate, rates } = useCurrency();
  const { coins, loading: coinsLoading, error: coinsError } = useCoinList();

  const [type,         setType]         = useState<TxType>('BUY');
  const [selectedCoin, setSelectedCoin] = useState<CoinListItem | null>(null);
  const [amount,       setAmount]       = useState('');
  const [price,        setPrice]        = useState('');
  const [date,         setDate]         = useState(today);
  const [wallet,       setWallet]       = useState('');
  // BUY/SELL trading fee — brokerage/commission, entered in the display currency
  const [fee,          setFee]          = useState('');
  // Transfer-specific fields
  const [fromWallet,   setFromWallet]   = useState('');
  const [toWallet,     setToWallet]     = useState('');
  const [networkFee,   setNetworkFee]   = useState('');
  const [notes,        setNotes]        = useState('');
  const [error,        setError]        = useState('');

  const knownWallets = useMemo(() => collectWallets(transactions), [transactions]);

  // Wallet names the user has actually used (from wallet / fromWallet / toWallet).
  // Same derivation as collectWallets, minus the presets — surfaced at the top of
  // the wallet dropdown so common wallets are one click away.
  const usedWallets = useMemo(() => {
    const set = new Set<string>();
    for (const tx of transactions) {
      if (tx.wallet)     set.add(tx.wallet);
      if (tx.fromWallet) set.add(tx.fromWallet);
      if (tx.toWallet)   set.add(tx.toWallet);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [transactions]);

  // Auto-fill price from market data when a coin is selected (BUY/SELL only).
  // `coin.currentPrice` is USD (app base unit); the Price field is in the selected
  // display currency, so convert with the same live rate the rest of the app uses.
  const handleCoinSelect = (coin: CoinListItem) => {
    setSelectedCoin(coin);
    if (!price && coin.currentPrice > 0 && type !== 'TRANSFER') {
      setPrice(String(parseFloat((coin.currentPrice * rate).toFixed(8))));
    }
  };

  // Live current price (USD) for the selected asset, for the "Current: …" helper:
  // the app-wide PriceMap for crypto & metals, falling back to the fetched snapshot
  // for stocks/ETFs (fetchStockSpot on select). 0 → helper hidden (no broken 0.00).
  const currentUsd = useMemo(() => {
    if (!selectedCoin) return 0;
    const mapUsd = prices[selectedCoin.id]?.usd;
    if (typeof mapUsd === 'number' && mapUsd > 0) return mapUsd;
    return selectedCoin.currentPrice > 0 ? selectedCoin.currentPrice : 0;
  }, [selectedCoin, prices]);

  // Per-coin balance in the chosen "From" wallet — used for the cap hint + validation
  // Date-aware: what can leave this wallet ON the transfer's own date without
  // sending any later point in its timeline negative. See utils/walletTimeline.
  const fromBalance = useMemo(() => {
    if (type !== 'TRANSFER' || !selectedCoin || !fromWallet) return 0;
    return maxSellableAt(transactions, selectedCoin.id, fromWallet, date);
  }, [type, selectedCoin, fromWallet, transactions, date]);

  // ── SELL: coins the user holds, as selectable assets (CHANGE 1) ─────────────
  // Sourced from the portfolio holdings hook (net balances, sample-mode aware).
  // Matched to the coin/metal lists for icons & prices; falls back to a cached
  // image for held assets outside the top-200 list. Sorted by holding value.
  const heldCoins = useMemo<CoinListItem[]>(() => {
    if (!holdings.length) return [];
    const cachedImages = loadCoinImages();
    return holdings
      .filter((h) => h.amount > 1e-9)
      .slice()
      .sort((a, b) => b.amount * b.currentPrice - a.amount * a.currentPrice)
      .map((h) => {
        const match = coins.find((c) => c.id === h.coinId) ?? metals.find((m) => m.id === h.coinId);
        if (match) return match;
        return {
          id: h.coinId,
          symbol: h.symbol,
          name: h.name,
          image: cachedImages[h.coinId] ?? '',
          marketCapRank: 0,
          currentPrice: h.currentPrice,
          marketCap: 0,
          volume24h: 0,
          high24h: 0,
          low24h: 0,
        } as CoinListItem;
      });
  }, [holdings, coins, metals]);

  const heldCoinIds = useMemo(() => new Set(heldCoins.map((c) => c.id)), [heldCoins]);

  // ── SELL: wallets holding the selected coin (CHANGE 2) ──────────────────────
  // Wallets that can fund a sell ON THE CHOSEN DATE, not wallets that happen to
  // hold the coin today. Built from every wallet that ever touched the coin,
  // because a wallet emptied since can still fund a back-dated sell.
  const sellWallets = useMemo(() => {
    if (type !== 'SELL' || !selectedCoin) return [];
    return walletsForCoin(transactions, selectedCoin.id)
      .map((w) => ({ wallet: w, balance: maxSellableAt(transactions, selectedCoin.id, w, date) }))
      .filter((x) => x.balance > WALLET_EPS)
      .sort((a, b) => b.balance - a.balance);
  }, [type, selectedCoin, transactions, date]);

  // ── SELL: available balance in the chosen wallet (CHANGE 3) ─────────────────
  const availableBalance = useMemo(() => {
    if (type !== 'SELL' || !selectedCoin || !wallet) return 0;
    return maxSellableAt(transactions, selectedCoin.id, wallet, date);
  }, [type, selectedCoin, wallet, transactions, date]);

  /** The limit message and its "Use max" action, shared by both types. */
  const limitFor = (w: string, max: number, verb: 'sell' | 'transfer') =>
    max > WALLET_EPS
      ? `You can ${verb} at most ${fmtCoins(max)} ${selectedCoin?.symbol ?? ''} from ${w} on ${formatDateAU(date)}.`
      : `You held no ${selectedCoin?.symbol ?? ''} in ${w} on ${formatDateAU(date)}.`;

  const sellAmountNum = parseFloat(amount);
  const sellOverLimit =
    type === 'SELL' && !!wallet && !!amount && !isNaN(sellAmountNum) &&
    sellAmountNum > availableBalance + WALLET_EPS;

  const transferOverLimit =
    type === 'TRANSFER' && !!fromWallet && !!amount && !isNaN(sellAmountNum) &&
    sellAmountNum > fromBalance + WALLET_EPS;

  // Auto-select the only wallet that holds the coin; clear a stale selection when
  // the wallet no longer holds the coin (SELL only).
  useEffect(() => {
    if (type !== 'SELL' || !selectedCoin) return;
    if (sellWallets.length === 1 && wallet !== sellWallets[0].wallet) {
      setWallet(sellWallets[0].wallet);
    } else if (wallet && !sellWallets.some((w) => w.wallet === wallet)) {
      setWallet('');
    }
  }, [type, selectedCoin, sellWallets, wallet]);

  const resetForm = () => {
    setAmount('');
    setPrice('');
    setDate(today);
    setWallet('');
    setFee('');
    setFromWallet('');
    setToWallet('');
    setNetworkFee('');
    setNotes('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const amt = parseFloat(amount);
    if (!selectedCoin)                     { setError('Select a coin.');         return; }
    if (!amount || isNaN(amt) || amt <= 0) { setError('Enter a valid amount.'); return; }
    if (!date)                             { setError('Select a date.');         return; }

    if (type === 'TRANSFER') {
      if (!fromWallet) { setError('Select a “From” wallet.'); return; }
      if (!toWallet)   { setError('Select a “To” wallet.');   return; }
      if (fromWallet === toWallet) {
        setError('From and To wallet cannot be the same.');
        return;
      }
      if (amt > fromBalance + WALLET_EPS) {
        setError(limitFor(fromWallet, fromBalance, 'transfer'));
        return;
      }
      const feeNum = networkFee ? parseFloat(networkFee) : 0;
      if (networkFee && (isNaN(feeNum) || feeNum < 0)) {
        setError('Network fee must be a non-negative number.');
        return;
      }
      // The fee field is in the selected currency; store USD (the app's unit) so it
      // lines up with the USD cost-basis math and the fmt() display (USD selected →
      // rate 1 → no change).
      //
      // A transfer has no price per unit, so the capture records the currency and
      // rates but no entryAmount. That is still enough to recover the fee as the
      // user typed it later: rawFee = networkFee / fxRateUsd.
      //
      // Rates are resolved for the transfer's OWN date, and the stored USD fee is
      // derived from that same rate. The `feeNum / rate` fallback applies only when
      // no rate resolved at all.
      const transferFx = await captureTransferFxForDate(currency.code, date, rates, feeNum);
      const feeUsd = feeNum > 0 ? transferFx.priceUsd ?? feeNum / rate : undefined;

      onAdd({
        coinId:     selectedCoin.id,
        symbol:     selectedCoin.symbol,
        name:       selectedCoin.name,
        type:       'TRANSFER',
        amount:     amt,
        price:      0,
        date,
        fromWallet,
        toWallet,
        networkFee: feeUsd,
        notes:      notes.trim() || undefined,
        ...transferFx.fx,
      });
      resetForm();
      return;
    }

    // BUY / SELL path. The Price field is in the SELECTED currency; it is stored as
    // USD (the app's storage unit).
    const prc = parseFloat(price);
    if (!price || isNaN(prc) || prc <= 0) { setError('Enter a valid price.'); return; }

    const feeNum = fee ? parseFloat(fee) : 0;
    if (fee && (isNaN(feeNum) || feeNum < 0)) {
      setError('Fee must be a non-negative number.');
      return;
    }

    // SELL: enforce selling from a wallet that holds the coin, within balance.
    if (type === 'SELL') {
      if (!wallet) { setError('Select a wallet to sell from.'); return; }
      if (amt > availableBalance + WALLET_EPS) {
        setError(limitFor(wallet, availableBalance, 'sell'));
        return;
      }
    }

    // Rates for the transaction's OWN date, and the USD price derived from those
    // same rates — so `price === entryAmount × fxRateUsd` holds by construction
    // rather than by coincidence. Only a row whose date resolves to no rate at all
    // falls back to converting at the live display rate.
    const entryFx = await captureEntryFxForDate(prc, currency.code, date, rates);
    const priceUsd = entryFx.priceUsd ?? prc / rate;

    // The fee is captured RAW (amount + currency) with its own derived USD/AUD
    // caches, resolved for the same date. Nothing overwrites the raw pair, so the
    // conversion stays re-resolvable. A zero fee records nothing at all.
    const feeFx = await captureFeeForDate(feeNum, currency.code, date, rates);

    onAdd({
      coinId: selectedCoin.id,
      symbol: selectedCoin.symbol,
      name:   selectedCoin.name,
      type,
      amount: amt,
      price:  priceUsd,
      date,
      wallet: wallet || undefined,
      ...entryFx.fx,
      ...feeFx,
    });

    resetForm();
    // Keep selectedCoin so next transaction is for the same coin
  };

  // Mobile sizing, all via max-md so desktop is untouched:
  //   text-[16px]  — anything smaller makes a mobile browser zoom the page on
  //                  focus, which then leaves the form scrolled sideways.
  //   min-h-[44px] — the tap target floor, kept while the padding tightens.
  const inputClass =
    'w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors' +
    ' max-md:text-[16px] max-md:px-3 max-md:py-2 max-md:min-h-[44px] max-md:rounded-lg';
  const selectClass =
    inputClass + ' [color-scheme:dark] cursor-pointer';
  const labelBase = 'block text-xs font-medium text-slate-400 uppercase tracking-wider max-md:text-[10px] max-md:tracking-normal';
  const labelClass = labelBase + ' mb-1.5 max-md:mb-1';
  // Row spans for the mobile two-column grid (the form is a plain stack on
  // desktop, so these are max-md only). Pairing Amount with Price, Date with
  // Wallet and From with To is what buys the height the sheet needs.
  const FULL = 'max-md:col-span-2';
  const HALF = 'max-md:col-span-1';

  const isTransfer = type === 'TRANSFER';
  // Named so the desktop paragraph and the mobile tooltip cannot drift apart.
  const feeHelp = type === 'BUY'
    ? 'Brokerage or commission. Added to your cost base, so it reduces the taxable gain.'
    : 'Brokerage or commission. Deducted from your proceeds, so it reduces the taxable gain.';
  const networkFeeHelp = 'Recorded as a cost. It does not reduce your coin count.';
  // Price is entered in the selected currency → convert to USD so `fmt` (which
  // multiplies USD by `rate`) renders the Total back in the selected currency.
  const feeTotalUsd = Math.max(0, parseFloat(fee) || 0) / rate;
  const grossTotalUsd = ((parseFloat(amount) || 0) * (parseFloat(price) || 0)) / rate;
  // The preview reads as cash out of pocket on a BUY and cash in on a SELL, which
  // is the same direction the fee moves the taxable gain.
  const totalUsd = isTransfer
    ? grossTotalUsd
    : type === 'BUY'
      ? grossTotalUsd + feeTotalUsd
      : grossTotalUsd - feeTotalUsd;
  const showTotal = !isTransfer && !!amount && !!price && isFinite(totalUsd) && totalUsd > 0;

  // Filter "To" options to exclude the chosen "From"
  const toOptions = knownWallets.filter((w) => w !== fromWallet);
  // Prepend any free-typed value not yet in the preset list (so it sticks in the dropdown)
  const fromOptionsFinal = fromWallet && !knownWallets.includes(fromWallet)
    ? [fromWallet, ...knownWallets]
    : knownWallets;
  const toOptionsFinal = toWallet && !toOptions.includes(toWallet) && toWallet !== fromWallet
    ? [toWallet, ...toOptions]
    : toOptions;

  // ── Submit gating ───────────────────────────────────────────────────────────
  // Disable the submit button until the required fields for the selected type are
  // filled and valid. Wallet assignment (BUY) is optional and never gates submit.
  const amountNum = parseFloat(amount);
  const priceNum = parseFloat(price);
  const amountValid = amount.trim() !== '' && !isNaN(amountNum) && amountNum > 0;
  const priceValid = price.trim() !== '' && !isNaN(priceNum) && priceNum >= 0;
  const baseValid = !!selectedCoin && amountValid && !!date;
  const canSubmit = isTransfer
    // TRANSFER: coin + amount(>0) + date + both wallets (distinct) + within balance.
    ? baseValid && !!fromWallet && !!toWallet && fromWallet !== toWallet && amountNum <= fromBalance + WALLET_EPS
    // BUY / SELL: coin + amount(>0) + price + date (and not over the sell balance).
    : baseValid && priceValid && !sellOverLimit;

  return (
    <div>
      <CardHeader title="Add Transaction" className="mb-5 max-md:mb-3" />
      <form onSubmit={handleSubmit} className="md:space-y-4 max-md:grid max-md:grid-cols-2 max-md:gap-2.5">

        {/* Buy / Sell / Transfer toggle */}
        <div className={FULL}>
          <label className={labelClass + ' max-md:sr-only'}>Type</label>
          <div className="flex rounded-xl overflow-hidden border border-slate-700 bg-slate-800">
            {(['BUY', 'SELL', 'TRANSFER'] as TxType[]).map((t) => {
              const active = type === t;
              const colour = t === 'BUY'
                ? (active ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:text-slate-300')
                : t === 'SELL'
                  ? (active ? 'bg-red-500 text-white' : 'text-slate-500 hover:text-slate-300')
                  : (active ? 'bg-blue-500 text-white' : 'text-slate-500 hover:text-slate-300');
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setType(t);
                    setError('');
                    if (t === 'SELL') {
                      // Clear SELL-incompatible state so the constrained dropdowns
                      // and validation start clean.
                      setWallet('');
                      setAmount('');
                      if (selectedCoin && !heldCoinIds.has(selectedCoin.id)) setSelectedCoin(null);
                    }
                  }}
                  className={`flex-1 py-2.5 text-sm font-semibold transition-all flex items-center justify-center gap-1.5 max-md:py-2 max-md:text-[13px] max-md:gap-1 max-md:min-h-[44px] ${colour}`}
                >
                  {/* The icon is what pushes TRANSFER against the right edge at
                      375px; the word alone is unambiguous. */}
                  {t === 'TRANSFER' && <ArrowRightLeft className="w-3.5 h-3.5 max-sm:hidden" />}
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        {/* Coin selector */}
        <div className={FULL}>
          <label className={labelClass}>Coin</label>
          <CoinSelector
            coins={coins}
            metals={metals}
            selectedId={selectedCoin?.id ?? ''}
            onSelect={handleCoinSelect}
            fallbackCoin={selectedCoin ?? undefined}
            loading={coinsLoading}
            error={coinsError}
            restrictTo={type === 'SELL' ? heldCoins : undefined}
            emptyMessage="No coins to sell yet. Add a BUY transaction first."
          />
        </div>

        {/* SELL: Wallet sits between Coin and Amount — pick the wallet first so the
            "Available" helper under Amount reflects the chosen wallet's balance.
            Constrained to wallets holding the selected coin (CHANGE 2). */}
        {type === 'SELL' && (
          <div className={FULL}>
            <label className={labelClass}>Wallet</label>
            <select
              value={wallet}
              onChange={(e) => setWallet(e.target.value)}
              disabled={!selectedCoin || sellWallets.length === 0}
              className={selectClass + ' disabled:opacity-50 disabled:cursor-not-allowed'}
            >
              <option value="">
                {!selectedCoin
                  ? 'Select a coin first'
                  : sellWallets.length === 0
                    ? 'No wallet held this coin on that date'
                    : 'Select a wallet…'}
              </option>
              {sellWallets.map((w) => (
                <option key={w.wallet} value={w.wallet}>
                  {w.wallet} · {fmtCoins(w.balance)} {selectedCoin?.symbol}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Amount */}
        <div className={isTransfer ? FULL : HALF}>
          <label className={labelClass}>
            Amount{selectedCoin ? ` (${selectedCoin.symbol})` : ''}
          </label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputClass}
            style={sellOverLimit ? { borderColor: 'rgba(196,97,94,0.5)', borderWidth: '0.5px' } : undefined}
          />
          {isTransfer && fromWallet && selectedCoin && transferOverLimit && (
            <p className="mt-1.5 max-md:mt-1 text-xs max-md:text-[11px] max-md:leading-snug" style={{ color: SELL_WARN_COLOR }}>
              {limitFor(fromWallet, fromBalance, 'transfer')}
              {fromBalance > WALLET_EPS && <UseMax onClick={() => setAmount(String(fromBalance))} />}
            </p>
          )}
          {isTransfer && fromWallet && selectedCoin && !transferOverLimit && (
            <p className="mt-1.5 text-xs text-slate-500">
              Available in <span className="text-slate-300">{fromWallet}</span> on {formatDateAU(date)}:{' '}
              <span className="text-slate-300 font-mono">{fmtCoins(fromBalance)} {selectedCoin.symbol}</span>
            </p>
          )}
          {/* SELL: real-time over-limit warning (CHANGE 3) */}
          {sellOverLimit && selectedCoin && (
            <p className="mt-1.5 max-md:mt-1 text-xs max-md:text-[11px] max-md:leading-snug" style={{ color: SELL_WARN_COLOR }}>
              {limitFor(wallet, availableBalance, 'sell')}
              {availableBalance > WALLET_EPS && <UseMax onClick={() => setAmount(String(availableBalance))} />}
            </p>
          )}
          {/* SELL: available-balance helper (shown when valid / no warning) */}
          {type === 'SELL' && selectedCoin && wallet && !sellOverLimit && (
            <p className="mt-1.5" style={{ color: SELL_HINT_COLOR, fontSize: 11 }}>
              Available on {formatDateAU(date)}: {fmtCoins(availableBalance)} {selectedCoin.symbol}
            </p>
          )}
        </div>

        {/* BUY / SELL: Price (entered in the selected display currency) */}
        {!isTransfer && (
          <div className={HALF}>
            <label className={labelClass}>Price per coin ({currency.code})</label>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              placeholder="0.00"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className={inputClass}
            />
            {/* Live market price for the selected asset (crypto, metal, stock/ETF),
                in the selected currency. Hidden until a positive price is known. */}
            {currentUsd > 0 && (
              <p className="mt-1.5 text-xs text-slate-500">
                Current: <span className="text-slate-300 font-mono">{fmt(currentUsd)}</span>
              </p>
            )}
          </div>
        )}

        {/* BUY / SELL: Fee (brokerage/commission, in the display currency) */}
        {!isTransfer && (
          <div className={FULL}>
            {/* The two-line explanation costs more height on a phone than the
                field itself. Below md it moves into the app's existing
                InfoTooltip, verbatim, so nothing is lost and it is one tap away;
                desktop keeps the paragraph exactly as it was. */}
            <div className="flex items-center gap-1.5 mb-1.5 max-md:mb-1">
              <label className={labelBase}>Fee ({currency.code}), optional</label>
              <span className="md:hidden"><InfoTooltip text={feeHelp} /></span>
            </div>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              placeholder="0.00"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              className={inputClass}
            />
            <p className="mt-1.5 text-xs text-slate-500 max-md:hidden">{feeHelp}</p>
          </div>
        )}

        {/* BUY / SELL: Total preview */}
        {showTotal && (
          <div className={`bg-slate-800/50 rounded-xl px-4 py-3 flex items-center justify-between max-md:py-2 ${FULL}`}>
            <span className="text-xs text-slate-500 uppercase tracking-wider">
              Total{feeTotalUsd > 0 ? (type === 'BUY' ? ' incl. fee' : ' net of fee') : ''}
            </span>
            <span className="text-sm font-semibold text-slate-200">{fmt(totalUsd)}</span>
          </div>
        )}

        {/* TRANSFER: From wallet */}
        {isTransfer && (
          <div className={HALF}>
            <label className={labelClass}>From Wallet</label>
            <select
              value={fromWallet}
              onChange={(e) => setFromWallet(e.target.value)}
              className={selectClass}
            >
              <option value="">Select source wallet…</option>
              {fromOptionsFinal.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          </div>
        )}

        {/* TRANSFER: To wallet */}
        {isTransfer && (
          <div className={HALF}>
            <label className={labelClass}>To Wallet</label>
            <select
              value={toWallet}
              onChange={(e) => setToWallet(e.target.value)}
              className={selectClass}
              disabled={!fromWallet}
            >
              <option value="">{fromWallet ? 'Select destination wallet…' : 'Select source wallet first'}</option>
              {toOptionsFinal.map((w) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          </div>
        )}

        {/* Date */}
        <div className={type === 'BUY' ? HALF : FULL}>
          <label className={labelClass}>Date</label>
          <input
            type="date"
            value={date}
            max={today}
            onChange={(e) => setDate(e.target.value)}
            className={inputClass + ' [color-scheme:dark]'}
          />
        </div>

        {/* BUY: Wallet (unchanged — any wallet) */}
        {type === 'BUY' && (
          <div className={HALF}>
            <label className={labelClass}>Wallet</label>
            <WalletSelector value={wallet} onChange={setWallet} usedWallets={usedWallets} />
          </div>
        )}

        {/* TRANSFER: Network fee + Notes */}
        {isTransfer && (
          <>
            <div className={HALF}>
              <div className="flex items-center gap-1.5 mb-1.5 max-md:mb-1">
                <label className={labelBase}>Network Fee ({currency.code})<span className="max-md:hidden">, optional</span></label>
                <span className="md:hidden"><InfoTooltip text={networkFeeHelp} /></span>
              </div>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                placeholder="0.00"
                value={networkFee}
                onChange={(e) => setNetworkFee(e.target.value)}
                className={inputClass}
              />
              <p className="mt-1.5 text-xs text-slate-500 max-md:hidden">{networkFeeHelp}</p>
            </div>
            <div className={HALF}>
              <label className={labelClass}>Notes, optional</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. cold storage"
                rows={2}
                className={inputClass + ' resize-none max-md:h-[44px]'}
              />
            </div>
            <div className={`flex items-start gap-2.5 p-3 rounded-xl bg-blue-500/5 border border-blue-500/20 max-md:gap-1.5 max-md:p-1.5 ${FULL}`}>
              <ArrowRightLeft className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-blue-200/70 leading-relaxed max-md:text-[11px] max-md:leading-snug">
                Transfers between your own wallets are <span className="font-semibold text-blue-300">non-taxable</span>.
                Total holdings stay the same. Only wallet balances change.
              </p>
            </div>
          </>
        )}

        {error && (
          <p className={`text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 ${FULL}`}>
            {error}
          </p>
        )}

        {/* Sticky on mobile so the button never scrolls away, which matters most
            when the on-screen keyboard is open and has eaten half the sheet. The
            negative margin bleeds the backing to the sheet edges, matching the
            body padding set in AddTransactionModal. */}
        <div className={`${FULL} max-md:sticky max-md:bottom-0 max-md:z-10 max-md:-mx-4 max-md:px-4 max-md:pt-2 max-md:pb-3 max-md:bg-slate-950 max-md:border-t max-md:border-slate-800`}>
          <button
            type="submit"
            disabled={!canSubmit}
            style={!canSubmit ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            className="w-full flex items-center justify-center gap-2 py-3 max-md:py-2.5 max-md:min-h-[44px] bg-white hover:bg-[#e8e8e8] active:bg-[#d8d8d8] text-[#0f0f0f] font-semibold rounded-xl transition-colors"
          >
            {isTransfer ? <ArrowRightLeft className="w-4 h-4" /> : <PlusCircle className="w-4 h-4" />}
            {isTransfer ? 'Add Transfer' : `Add ${type} Transaction`}
          </button>
        </div>
      </form>
    </div>
  );
}
